import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import './RefurbishedVsNewArticle.css';
import BLOG_ARTICLES from '../../../data/blogArticles';
import { getPrevNext } from '../../../utils/blogHelpers';

/* ─── hero image (laptop on a clean desk) ─── */
const HERO_IMG = 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?auto=format&fit=crop&w=1600&q=80';

/* ─── TOC entries ─── */
const TOC = [
  'Introduction',
  'Understanding Refurbished Laptops',
  'Quality Standards & Certification',
  'Cost Analysis: Breaking Down the Numbers',
  'Performance & Reliability Comparison',
  'Warranty & Support Considerations',
  'Environmental Impact & Sustainability',
  'Decision Framework: Which Option Suits You?',
  'Making the Smart Investment Choice',
];

/* ─── tags ─── */
const TAGS = ['Laptops', 'Investment', 'Technology', 'Budget', 'Refurbished'];

/* ─── scroll-reveal hook ─── */
function useReveal(ref) {
  useEffect(() => {
    const els = ref.current?.querySelectorAll('.rvn-reveal');
    if (!els) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('rvn-visible'); obs.unobserve(e.target); }
      }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ═══════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════ */
export default function RefurbishedVsNewArticle() {
  const rootRef = useRef(null);
  const heroImgRef = useRef(null);
  const navigate = useNavigate();
  useReveal(rootRef);

  /* parallax */
  useEffect(() => {
    const onScroll = () => {
      if (!heroImgRef.current) return;
      heroImgRef.current.style.transform = `translateY(${window.scrollY * 0.28}px)`;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* smooth-scroll to heading */
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <article className="rvn-article" ref={rootRef}>
      <Helmet>
        <title>Refurbished vs Brand New Laptops — Which is the Right Investment? | Nexawave</title>
        <meta name="description" content="Making the smart choice between refurbished and brand new laptops requires understanding value, performance, and long-term benefits. Buy refurbished laptops online in India." />
        <link rel="canonical" href="https://nexawave.com/blog/refurbished-vs-new" />
        <meta property="og:title" content="Refurbished vs Brand New Laptops — Which is the Right Investment?" />
        <meta property="og:description" content="Making the smart choice between refurbished and brand new laptops requires understanding value, performance, and long-term benefits." />
        <meta property="og:image" content={HERO_IMG} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              "headline": "Refurbished vs Brand New Laptops",
              "image": "${HERO_IMG}",
              "author": {
                "@type": "Person",
                "name": "Arjun Sharma"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Nexawave",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://nexawave.com/logo.png"
                }
              },
              "datePublished": "2024-12-15"
            }
          `}
        </script>
      </Helmet>

      {/* ── HERO ── */}
      <section className="rvn-hero">
        <div ref={heroImgRef} className="rvn-hero-img-wrap">
          <img src={HERO_IMG} alt="Refurbished business laptop on a clean desk comparing used vs new performance in India" />
        </div>
        <div className="rvn-hero-fade" />
      </section>

      {/* ── HERO TEXT ── */}
      <div className="rvn-hero-text">
        <span className="rvn-label" style={{ display: 'block', marginBottom: '1.2rem' }}>
          <span className="rvn-seal-dot" />
          Tech Insights · Buying Guide
        </span>

        <h1>
          Refurbished vs Brand New:{' '}
          <em>Which Laptop is the Right Investment?</em>
        </h1>

        <p className="rvn-hero-subtitle">
          Making the smart choice between refurbished and brand new laptops requires understanding value, performance, and long-term benefits.
        </p>

        <div className="rvn-divider" />
      </div>

      {/* ── BODY WRAPPER ── */}
      <div className="rvn-body">

        {/* Author bar */}
        <div className="rvn-author-bar rvn-reveal">
          <div className="rvn-author-avatar">👨‍💻</div>
          <div>
            <div className="rvn-author-name">Arjun Sharma</div>
            <div className="rvn-author-role">Senior Tech Expert &amp; Hardware Specialist with 8+ years in laptop refurbishment industry</div>
            <div className="rvn-author-meta">Dec 15, 2024 · 8 min read</div>
          </div>
        </div>

        {/* Share */}
        <div className="rvn-share rvn-reveal rvn-reveal-d1">
          <span className="rvn-share-label">Share</span>
          <button className="rvn-share-btn" title="Twitter" onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}`, '_blank')}>𝕏</button>
          <button className="rvn-share-btn" title="LinkedIn" onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank')}>in</button>
          <button className="rvn-share-btn" title="Copy link" onClick={() => { navigator.clipboard.writeText(window.location.href); }}>🔗</button>
        </div>

        {/* Tags */}
        <div className="rvn-tags rvn-reveal rvn-reveal-d2">
          {TAGS.map((t) => <span className="rvn-tag" key={t}>#{t}</span>)}
        </div>

        {/* TOC */}
        <nav className="rvn-toc rvn-reveal rvn-reveal-d3">
          <div className="rvn-toc-title">Table of Contents</div>
          <ol>
            {TOC.map((item, i) => (
              <li key={i} onClick={() => scrollTo(`rvn-s${i}`)}>
                <a href={`#rvn-s${i}`} onClick={(e) => e.preventDefault()}>{item}</a>
              </li>
            ))}
          </ol>
        </nav>

        {/* ═══ SECTIONS ═══ */}

        {/* 0 — Introduction */}
        <section id="rvn-s0" className="rvn-reveal">
          <h2><span className="rvn-section-num">01/</span> Introduction</h2>
          <p>In today's technology-driven world, choosing the right laptop represents a significant investment decision that extends far beyond the initial purchase price. With the growing popularity of refurbished devices and the constant allure of brand-new technology, consumers face an increasingly complex decision matrix when selecting their next computing companion.</p>
          <p>The laptop market has evolved dramatically over the past decade, with <Link to="/laptops" style={{textDecoration: 'underline'}}>refurbished devices</Link> emerging as a legitimate alternative to brand-new purchases. This shift isn't merely about cost savings — it represents a fundamental change in how we approach technology consumption, sustainability, and value optimization.</p>
          <p>Whether you're a student managing a tight budget, a professional seeking maximum performance per dollar, or an environmentally conscious consumer looking to reduce electronic waste, understanding the nuanced differences between refurbished and brand-new laptops is crucial for making an informed investment decision.</p>
        </section>

        {/* 1 — Understanding Refurbished */}
        <section id="rvn-s1" className="rvn-reveal">
          <h2><span className="rvn-section-num">02/</span> Understanding Refurbished Laptops</h2>
          <p>Refurbished laptops represent a category of pre-owned devices that have undergone comprehensive restoration processes to return them to like-new condition. Unlike simple used laptops sold "as-is," refurbished devices follow strict quality control protocols that ensure reliability and performance standards.</p>
          <p>The refurbishment process typically involves multiple stages of inspection, testing, cleaning, and component replacement. Professional refurbishers employ certified technicians who systematically evaluate every aspect of the device, from hardware functionality to cosmetic condition.</p>

          <h3>Common Sources of Refurbished Laptops</h3>
          <div className="rvn-cards-grid">
            <div className="rvn-info-card">
              <div className="rvn-info-card-title">Corporate Lease Returns</div>
              <div className="rvn-info-card-desc">High-quality business laptops returned after lease periods</div>
            </div>
            <div className="rvn-info-card">
              <div className="rvn-info-card-title">Manufacturer Returns</div>
              <div className="rvn-info-card-desc">Devices returned within return windows, often with minimal usage</div>
            </div>
            <div className="rvn-info-card">
              <div className="rvn-info-card-title">Display Models</div>
              <div className="rvn-info-card-desc">Store demonstration units with limited operational hours</div>
            </div>
            <div className="rvn-info-card">
              <div className="rvn-info-card-title">Overstock Items</div>
              <div className="rvn-info-card-desc">New devices sold as refurbished due to packaging damage or minor cosmetic issues</div>
            </div>
          </div>
        </section>

        {/* 2 — Quality Standards */}
        <section id="rvn-s2" className="rvn-reveal">
          <h2><span className="rvn-section-num">03/</span> Quality Standards &amp; Certification</h2>
          <p>Reputable refurbishment programs maintain rigorous quality standards that often exceed those of typical used device sales. These standards encompass both functional and aesthetic criteria, ensuring customers receive devices that perform reliably while maintaining professional appearance standards.</p>
          <p>Professional refurbishers typically follow industry-standard testing protocols that include comprehensive hardware diagnostics, stress testing, and quality assurance verification. Many programs also provide detailed condition reports and certification documentation that outline the specific refurbishment procedures performed on each device.</p>
        </section>

        {/* 3 — Cost Analysis */}
        <section id="rvn-s3" className="rvn-reveal">
          <h2><span className="rvn-section-num">04/</span> Cost Analysis: Breaking Down the Numbers</h2>
          <p>The financial implications of choosing between refurbished and brand-new laptops extend well beyond the initial purchase price. A comprehensive cost analysis must consider total cost of ownership, including potential maintenance, upgrade costs, and depreciation rates over the device's useful life.</p>

          <h3>Upfront Investment Comparison</h3>
          <p>Refurbished laptops typically offer savings ranging from 30% to 70% compared to equivalent brand-new models, depending on factors such as age, condition, and market demand.</p>

          <div className="rvn-table-hint">Swipe to see more →</div>
          <div className="rvn-table-wrap">
            <table className="rvn-table">
              <thead>
                <tr>
                  <th>Condition</th>
                  <th>Typical Price</th>
                  <th>Savings</th>
                  <th>Value Proposition</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td data-label="Condition"><strong>Brand New</strong></td>
                  <td data-label="Typical Price">₹95,000 – ₹1,50,000</td>
                  <td data-label="Savings">—</td>
                  <td data-label="Value Proposition">Latest features, full warranty</td>
                </tr>
                <tr>
                  <td data-label="Condition"><strong>Certified Refurbished</strong></td>
                  <td data-label="Typical Price">₹48,000 – ₹75,000</td>
                  <td data-label="Savings">40-50%</td>
                  <td data-label="Value Proposition">Proven reliability, warranty included</td>
                </tr>
                <tr>
                  <td data-label="Condition"><strong>Good Condition Used</strong></td>
                  <td data-label="Typical Price">₹32,000 – ₹54,000</td>
                  <td data-label="Savings">60-70%</td>
                  <td data-label="Value Proposition">Maximum savings, limited warranty</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Long-term Value Proposition</h3>
          <p>While refurbished laptops offer immediate cost savings, their long-term value proposition depends on several factors including build quality, remaining useful life, and upgrade potential. High-quality refurbished business laptops often provide exceptional long-term value due to their robust construction and enterprise-grade components.</p>
          <p>Depreciation curves for refurbished devices tend to be more favorable than brand-new laptops, as the steepest depreciation has already occurred. This characteristic makes refurbished laptops particularly attractive for users who plan to resell or upgrade their devices within 2-3 years.</p>
        </section>

        {/* 4 — Performance */}
        <section id="rvn-s4" className="rvn-reveal">
          <h2><span className="rvn-section-num">05/</span> Performance &amp; Reliability Comparison</h2>
          <p>Modern refurbished laptops, particularly those from reputable refurbishers, often deliver performance that's virtually indistinguishable from brand-new equivalents. The key differentiator lies not in raw performance capabilities, but in factors such as battery life, component wear, and long-term reliability expectations.</p>
          <p>Professional-grade <Link to="/laptops?category=business" style={{textDecoration: 'underline'}}>refurbished business laptops</Link> frequently outperform consumer-grade new laptops in build quality and durability. Enterprise laptops are designed for intensive daily use and often feature superior cooling systems, more robust keyboards, and higher-quality displays compared to consumer alternatives.</p>

          <h3>Performance Considerations</h3>
          <div className="rvn-cards-grid">
            <div className="rvn-info-card">
              <div className="rvn-info-card-title">Processing Power</div>
              <div className="rvn-info-card-desc">Identical performance when comparing same-generation processors</div>
            </div>
            <div className="rvn-info-card">
              <div className="rvn-info-card-title">Memory &amp; Storage</div>
              <div className="rvn-info-card-desc">Often upgradeable; refurbished units may include premium configurations</div>
            </div>
            <div className="rvn-info-card">
              <div className="rvn-info-card-title">Battery Life</div>
              <div className="rvn-info-card-desc">May be reduced; quality refurbishers often replace batteries</div>
            </div>
            <div className="rvn-info-card">
              <div className="rvn-info-card-title">Display Quality</div>
              <div className="rvn-info-card-desc">Business laptops often feature superior displays compared to consumer models</div>
            </div>
          </div>
        </section>

        {/* 5 — Warranty */}
        <section id="rvn-s5" className="rvn-reveal">
          <h2><span className="rvn-section-num">06/</span> Warranty &amp; Support Considerations</h2>
          <p>Warranty coverage represents a critical differentiator between refurbished and brand-new laptop purchases. While new laptops typically include comprehensive manufacturer warranties ranging from one to three years, refurbished devices offer varying warranty terms depending on the seller and refurbishment quality.</p>
          <p>Reputable refurbishers often provide warranties ranging from 90 days to one year, with some premium refurbishment programs offering extended warranty options.</p>

          <div className="rvn-warranty-grid">
            <div className="rvn-warranty-card rvn-warranty-card--new">
              <h4>Brand New Laptops</h4>
              <ul>
                <li>1-3 year manufacturer warranty</li>
                <li>Comprehensive hardware coverage</li>
                <li>Technical support included</li>
                <li>Accidental damage options available</li>
              </ul>
            </div>
            <div className="rvn-warranty-card rvn-warranty-card--refurb">
              <h4>Certified Refurbished</h4>
              <ul>
                <li>90 days to 1 year warranty</li>
                <li>Hardware defect coverage</li>
                <li>Return/exchange policies</li>
                <li>Limited technical support</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 6 — Environmental */}
        <section id="rvn-s6" className="rvn-reveal">
          <h2><span className="rvn-section-num">07/</span> Environmental Impact &amp; Sustainability</h2>
          <p>The environmental implications of laptop purchasing decisions have gained increasing importance as consumers become more conscious of electronic waste and carbon footprint considerations. Choosing refurbished laptops represents a significant opportunity to reduce environmental impact while obtaining high-quality computing devices.</p>
          <p>Electronic waste (e-waste) represents one of the fastest-growing waste streams globally, with millions of laptops discarded annually despite remaining functional. By extending the useful life of existing devices through refurbishment, consumers can significantly reduce their environmental footprint.</p>

          <div className="rvn-stats-grid">
            <div className="rvn-stat-card">
              <div className="rvn-stat-icon">🌱</div>
              <div className="rvn-stat-title">Carbon Footprint Reduction</div>
              <div className="rvn-stat-desc">Up to 70% lower CO₂ emissions compared to new laptop production</div>
            </div>
            <div className="rvn-stat-card">
              <div className="rvn-stat-icon">⛏️</div>
              <div className="rvn-stat-title">Resource Conservation</div>
              <div className="rvn-stat-desc">Saves rare earth minerals and reduces mining requirements</div>
            </div>
            <div className="rvn-stat-card">
              <div className="rvn-stat-icon">♻️</div>
              <div className="rvn-stat-title">Waste Reduction</div>
              <div className="rvn-stat-desc">Prevents functional devices from entering landfills prematurely</div>
            </div>
            <div className="rvn-stat-card">
              <div className="rvn-stat-icon">⚡</div>
              <div className="rvn-stat-title">Energy Savings</div>
              <div className="rvn-stat-desc">Eliminates energy-intensive manufacturing processes</div>
            </div>
          </div>
        </section>

        {/* 7 — Decision Framework */}
        <section id="rvn-s7" className="rvn-reveal">
          <h2><span className="rvn-section-num">08/</span> Decision Framework: Which Option Suits You?</h2>
          <p>Selecting between refurbished and brand-new laptops requires careful consideration of individual needs, priorities, and circumstances. The optimal choice varies significantly based on factors such as budget constraints, performance requirements, risk tolerance, and intended usage patterns.</p>

          <div className="rvn-decision-grid">
            <div className="rvn-decision-card rvn-decision-card--refurb">
              <h4>Choose Refurbished When</h4>
              <ul>
                <li>Budget optimization is a primary concern</li>
                <li>You need proven, reliable performance over cutting-edge features</li>
                <li>Environmental sustainability is important to you</li>
                <li>You're comfortable with shorter warranty periods</li>
                <li>You plan to upgrade within 2-3 years</li>
                <li>You need business-grade build quality at consumer prices</li>
              </ul>
            </div>
            <div className="rvn-decision-card rvn-decision-card--new">
              <h4>Choose Brand New When</h4>
              <ul>
                <li>You require the latest technology and features</li>
                <li>Maximum warranty coverage is essential</li>
                <li>You plan to keep the device for 4+ years</li>
                <li>You need specific configurations not available refurbished</li>
                <li>Peace of mind regarding device history is important</li>
                <li>You require manufacturer technical support</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 8 — Smart Choice */}
        <section id="rvn-s8" className="rvn-reveal">
          <h2><span className="rvn-section-num">09/</span> Making the Smart Investment Choice</h2>
          <p>The decision between refurbished and brand-new laptops ultimately depends on your specific needs, budget, and priorities. Both options offer distinct advantages that can represent smart investments under different circumstances.</p>
          <p>Refurbished laptops excel in providing exceptional value for budget-conscious consumers, environmentally aware buyers, and users who prioritize proven performance over cutting-edge features. The significant cost savings, combined with reliable performance and reduced environmental impact, make refurbished devices an increasingly attractive option for many users.</p>
          <p>Brand-new laptops remain the optimal choice for users who require the latest technology, maximum warranty coverage, and the peace of mind that comes with being the first owner. The premium paid for new devices often translates to longer useful life, comprehensive support, and access to the newest features and capabilities.</p>
          <p>Regardless of your choice, the key to making a smart investment lies in purchasing from reputable sellers, understanding warranty terms, and selecting devices that align with your specific needs and usage patterns. Both refurbished and brand-new laptops can provide excellent value when chosen thoughtfully and purchased from trusted sources.</p>

          {/* Final recommendation */}
          <div className="rvn-recommendation">
            <div className="rvn-recommendation-label">Final Investment Recommendation</div>
            <p>For most users, certified refurbished laptops from reputable sellers represent the optimal balance of performance, value, and reliability. The substantial cost savings, combined with proven performance and environmental benefits, make refurbished devices an intelligent investment choice for the majority of laptop buyers.</p>
          </div>
        </section>

        <hr className="rvn-section-rule" style={{ margin: '3rem 0' }} />

        {/* Tags bottom */}
        <div className="rvn-tags rvn-reveal">
          {TAGS.map((t) => <span className="rvn-tag" key={t}>#{t}</span>)}
        </div>

        {/* CTA */}
        <div className="rvn-cta rvn-reveal">
          <h3>Ready to Find Your Perfect Laptop?</h3>
          <p>Explore our curated selection of premium refurbished and new laptops, carefully selected for quality and value.</p>
          <div className="rvn-cta-buttons">
            <Link to="/laptops" className="rvn-btn rvn-btn--primary" style={{textDecoration:'none'}}>Browse Refurbished Laptops</Link>
            <Link to="/laptops" className="rvn-btn rvn-btn--outline" style={{textDecoration:'none'}}>View New Laptops</Link>
          </div>
        </div>

        {/* Nav articles */}
        <div className="rvn-nav-articles rvn-reveal">
          {(() => {
            const { prev, next } = getPrevNext('refurbished-vs-new', BLOG_ARTICLES);
            return (
              <>
                <Link to={prev ? `/blog/${prev.slug}` : '/blog'} className="rvn-nav-card" style={{textDecoration:'none', color:'inherit'}}>
                  <div className="rvn-nav-card-dir">← Previous Article</div>
                  <div className="rvn-nav-card-title">{prev ? prev.title : 'Back to Articles'}</div>
                  <div className="rvn-nav-card-excerpt">{prev ? prev.excerpt : 'Explore more articles on Nexawave.'}</div>
                </Link>
                <Link to={next ? `/blog/${next.slug}` : '/blog'} className="rvn-nav-card" style={{textDecoration:'none', color:'inherit'}}>
                  <div className="rvn-nav-card-dir">Next Article →</div>
                  <div className="rvn-nav-card-title">{next ? next.title : 'More Articles'}</div>
                  <div className="rvn-nav-card-excerpt">{next ? next.excerpt : 'Discover additional guides and insights.'}</div>
                </Link>
              </>
            );
          })()}
        </div>

        <div style={{ textAlign: 'center', margin: '2rem 0 3rem' }}>
          <Link to="/blog" className="rvn-btn rvn-btn--primary" style={{ background: 'var(--rvn-accent-light)', textDecoration:'none' }}>
            View All Articles
          </Link>
        </div>
      </div>
    </article>
  );
}
